import { reactive, ref, computed } from 'vue'

/**
 * Tenant custom emoji catalog + picker-search helper.
 *
 * Lifecycle:
 *   - `ensureLoaded()` pulls the full tenant catalog once per session and
 *     indexes it into a reactive Map<name, emoji> that `ChatMessageItem`
 *     reads synchronously to resolve `:shortcode:` tokens into <img>s.
 *   - `search(query)` hits the server's unified search endpoint (tenant
 *     catalog + slackmojis.com scrape).
 *   - `pick(slackmojisEntry)` persists an upstream emoji to our S3 + DB
 *     and returns the freshly-added catalog entry. The local map is
 *     updated synchronously so the composer + any currently-rendered
 *     messages see the new entry instantly.
 *
 * This module is intentionally a singleton — every component that needs
 * to resolve or search shares the same in-memory state, so there's only
 * one `/api/chat/emojis` round-trip per page load.
 */

export interface EmojiEntry {
    id: string
    name: string
    url: string
    slackmojisId: string | null
    useCount: number
}

export interface SlackmojiSearchHit {
    id: string          // upstream slackmojis id
    name: string
    imageUrl: string
    title: string
}

interface SearchResult {
    query: string
    tenantHits: EmojiEntry[]
    slackmojisHits: SlackmojiSearchHit[]
}

const catalog = reactive(new Map<string, EmojiEntry>())
const loaded = ref(false)
const loading = ref(false)

// Match any `:a-zA-Z0-9_-: ` token. Kept loose; the renderer skips tokens
// that don't resolve to a known emoji. Shared with ChatMessageItem.
export const EMOJI_TOKEN_RE = /:([a-zA-Z0-9_-]{1,48}):/g

function put(e: EmojiEntry) {
    catalog.set(e.name.toLowerCase(), e)
}

/** Resolve a shortcode to its image URL, or null when unknown. */
export function resolveEmoji(name: string): EmojiEntry | null {
    return catalog.get(name.toLowerCase()) || null
}

async function ensureLoaded(): Promise<void> {
    if (loaded.value || loading.value) return
    loading.value = true
    try {
        const res = await $fetch<{ emojis: EmojiEntry[] }>('/api/chat/emojis')
        for (const e of (res.emojis || [])) put(e)
        loaded.value = true
    } catch (err) {
        console.error('[chat:emojis] load failed', err)
    } finally {
        loading.value = false
    }
}

async function search(query: string): Promise<SearchResult> {
    const res = await $fetch<SearchResult>('/api/chat/emojis/search', {
        params: { q: query }
    })
    // Side-effect: merge tenant hits into the local catalog so any unloaded
    // entries surface without waiting for a separate fetch.
    for (const e of (res.tenantHits || [])) put(e)
    return res
}

/**
 * Download the upstream emoji to our S3 + insert into the catalog. Safe to
 * call twice for the same hit — the server is idempotent and bumps useCount
 * for existing entries.
 */
async function pick(hit: SlackmojiSearchHit | { slackmojisId: string; name: string; sourceUrl: string }): Promise<EmojiEntry> {
    const body = 'slackmojisId' in hit
        ? { slackmojisId: hit.slackmojisId, name: hit.name, sourceUrl: hit.sourceUrl }
        : { slackmojisId: hit.id, name: hit.name, sourceUrl: hit.imageUrl }
    const res = await $fetch<{ ok: boolean; emoji: EmojiEntry }>('/api/chat/emojis/pick', {
        method: 'POST',
        body
    })
    put(res.emoji)
    return res.emoji
}

/**
 * Autocomplete against the locally loaded catalog. For the picker's
 * server-side search use `search()` instead — that one also returns
 * slackmojis hits.
 */
function suggestLocal(prefix: string, limit = 8): EmojiEntry[] {
    const p = prefix.toLowerCase()
    const out: EmojiEntry[] = []
    for (const e of catalog.values()) {
        if (e.name.toLowerCase().startsWith(p)) out.push(e)
        if (out.length >= limit) break
    }
    // Sort exact-match first, then by useCount
    out.sort((a, b) => {
        if (a.name === p) return -1
        if (b.name === p) return 1
        return (b.useCount || 0) - (a.useCount || 0)
    })
    return out
}

export function useCustomEmoji() {
    return {
        ensureLoaded,
        search,
        pick,
        resolveEmoji,
        suggestLocal,
        catalog: computed(() => catalog),
        loaded: computed(() => loaded.value),
        loading: computed(() => loading.value)
    }
}
